import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPopoverAttached } from '@/core/ButtonWithPopoverAttached.molecule';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { Feature } from '@/licenseManagement/licenseManagement.store';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { isScalingTableEditorEnabled } from '@/services/systemConfiguration.utilities';
import { resetTableDefinition, setShowCreateModel } from '@/tableDefinitionEditor/tableDefinition.actions';
import { EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { resetTableEditorStore } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { CreateApplyInScalingTableModal } from '@/tools/models/CreateApplyInScalingTableModal.molecule';
import { resetModel } from '@/tools/models/model.actions';
import { MODEL_TYPES } from '@/tools/models/model.types';
import { setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { Button } from '@seeqdev/qomponents';
import type { SelectedSearchItem } from '@/search/search.types';

export const CreateModelButton = () => {
  const { t } = useTranslation();

  const [isItemSearchModalOpen, setIsItemSearchModalOpen] = useState(false);
  const [selectedScalingTable, setSelectedScalingTable] = useState<SelectedSearchItem | undefined>(undefined);
  const editorType = EDITOR_TYPES.SCALING;

  const cancelScalingTableSelection = () => {
    setIsItemSearchModalOpen(false);
    setSelectedScalingTable(undefined);
  };

  const createFromItems = () => {
    resetModel(MODEL_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODEL_CREATE);
  };

  return (
    <>
      {isScalingTableEditorEnabled() && sqLicenseManagementStore.hasValidFeature(Feature.Scaling_Tables) ? (
        <ButtonWithPopoverAttached
          id="createModel"
          label="CREATE"
          testId="createModel"
          onClick={createFromItems}
          showPopover={true}
          popoverContent={
            <div className="flexRowContainer tw:p-2 tw:gap-1">
              <Button
                onClick={createFromItems}
                label={t('MODELS.CREATE_FROM_ITEMS')}
                size="sm"
                variant="outline"
                tooltip={t('MODELS.CREATE_FROM_ITEMS_TOOLTIP')}
                tooltipOptions={{ position: 'right', delay: 0 }}
                testId="createModelFromItemsButton"
                extraClassNames="cursorPointer pl20 pr20 width-maximum fs14 tw:border-0 textAlignLeft noActiveBg"
              />
              <Button
                onClick={() => setIsItemSearchModalOpen(true)}
                label={t('MODELS.CREATE_FROM_SCALING_TABLE')}
                size="sm"
                variant="outline"
                tooltip={t('MODELS.CREATE_FROM_SCALING_TABLE_TOOLTIP')}
                tooltipOptions={{ position: 'right', delay: 0 }}
                testId="createModelFromScalingTableButton"
                extraClassNames="cursorPointer pl20 pr20 width-maximum fs14 tw:border-0 textAlignLeft noActiveBg"
              />
            </div>
          }
        />
      ) : (
        <Button
          label={t('CREATE')}
          testId="createModelButton"
          onClick={createFromItems}
          tooltip={t('MODELS.CREATE_FROM_ITEMS_TOOLTIP')}
          tooltipOptions={{ position: 'right', delay: 0 }}
        />
      )}
      {isItemSearchModalOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <CreateApplyInScalingTableModal
            header="MODELS.MODAL_HEADER"
            helpText="MODELS.CREATE_MODAL_HELP_TEXT"
            onClose={cancelScalingTableSelection}
            onSubmit={() => {
              setIsItemSearchModalOpen(false);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              if (selectedScalingTable) {
                editTableDefinition(selectedScalingTable, editorType).then(() => {
                  setShowCreateModel(editorType, true);
                });
              }
              setSelectedScalingTable(undefined);
            }}
            onCreateNewItem={() => {
              setIsItemSearchModalOpen(false);
              resetItemFinder();
              resetTableDefinition(editorType);
              resetTableEditorStore(editorType);
              setDisplayTableDefinitionEditor(true);
              setAutoFocusTableDefinitionTab(true);
              setShowCreateModel(editorType, true);
            }}
            onSelectItem={(item) => setSelectedScalingTable(item)}
            selectedItem={selectedScalingTable}
            iconPartialTooltipKey="MODELS.MODEL_CREATE"
          />
        </div>
      )}
    </>
  );
};
